import type { TFunction } from "i18next";
import { MonitoringAlert } from "src/Types/alerts.types";
import { formatCalendarDay } from "src/i18n/format";

type AlertText = { title: string; message: string };

const providerName = (alert: MonitoringAlert, t: TFunction) =>
  alert.listing?.provider === "VRBO"
    ? t("dashboard.providerVrbo")
    : t("dashboard.providerAirbnb");

const isVrbo = (alert: MonitoringAlert) => alert.listing?.provider === "VRBO";

/**
 * Localized title/message for an alert. Listing titles are the provider's
 * listing name and stay verbatim; our own copy is rebuilt from the alert type.
 * Older alerts missing the structured details fall back to the stored text.
 */
export const getAlertText = (alert: MonitoringAlert, t: TFunction): AlertText => {
  const details = alert.details ?? {};
  switch (alert.type) {
    case "NEW_LISTING":
      return {
        title: alert.title,
        message: t("alerts.newListing", {
          listingType:
            alert.listing?.roomType ||
            t("alerts.providerListing", { provider: providerName(alert, t) }),
        }),
      };
    case "NEARBY_LISTING":
      return {
        title: alert.title,
        message: t(isVrbo(alert) ? "alerts.nearbyVrbo" : "alerts.nearbyAirbnb"),
      };
    case "LISTING_REAPPEARED":
      return {
        title: alert.title,
        message: t("alerts.reappeared", { provider: providerName(alert, t) }),
      };
    case "LISTING_CHANGED":
      return { title: alert.title, message: t("alerts.changed") };
    case "LISTING_REMOVED":
      return {
        title: alert.title,
        message:
          typeof details.missedScans === "number"
            ? t("alerts.removed", { count: details.missedScans })
            : alert.message,
      };
    case "AVAILABILITY_CHANGED":
      return {
        title: alert.title,
        message: Array.isArray(details.becameUnavailable)
          ? t("alerts.availabilityChanged", {
              count: details.becameUnavailable.length,
            })
          : alert.message,
      };
    case "SCAN_FAILED":
      return {
        title: t("alerts.scanFailedTitle"),
        message: t("alerts.scanFailed"),
      };
    case "SCAN_COMPLETED":
      return {
        title: t("alerts.scanCompletedTitle"),
        message:
          typeof details.listingCount === "number"
            ? details.errorCount
              ? t("alerts.scanCompletedWithErrors", {
                  count: details.listingCount,
                  errors: details.errorCount,
                })
              : t("alerts.scanCompleted", { count: details.listingCount })
            : alert.message,
      };
    default: {
      const unhandled: never = alert.type;
      void unhandled;
      return { title: alert.title, message: alert.message };
    }
  }
};

/** Full card detail: message plus unavailable dates and the listing's price. */
export const getAlertDetail = (alert: MonitoringAlert, t: TFunction): string => {
  const { message } = getAlertText(alert, t);
  const dates = alert.details?.becameUnavailable || [];
  const shown = dates.slice(0, 3).map((day) => formatCalendarDay(day)).join(", ");
  const more =
    dates.length > 3 ? ` ${t("alerts.moreDates", { count: dates.length - 3 })}` : "";
  const dateSummary = dates.length > 0 ? ` ${shown}${more}.` : "";
  const price = alert.listing?.price
    ? ` ${alert.listing.price}${alert.listing.priceQualifier ? ` ${alert.listing.priceQualifier}` : ""}`
    : "";
  return `${message}${dateSummary}${price}`;
};
